function getPebbleTrack(scroller) {
  return scroller.querySelector('[data-pebble-track]') || scroller.querySelector('.pebble-collections__track');
}

function getPebbleSlide(scroller) {
  return scroller.querySelector('[data-pebble-slide]') || scroller.querySelector('.pebble-collections__item');
}

function getPebbleProgressTrack(scroller) {
  const panel = scroller.closest('[data-pebble-panel]') || scroller.parentElement;
  if (!panel) return null;
  return panel.querySelector('.pebble-collection-nav__progress') || panel.querySelector('.pebble-collections__progress');
}

function getPebbleProgressThumb(panel) {
  return (
    panel.querySelector('.pebble-collection-nav__progress-thumb') ||
    panel.querySelector('.pebble-collections__progress-thumb')
  );
}

function updatePebbleProgress(scroller, thumb) {
  if (!scroller || !thumb) return;

  const maxScroll = scroller.scrollWidth - scroller.clientWidth;
  if (maxScroll <= 0) {
    thumb.style.width = '100%';
    thumb.style.transform = 'translateX(0)';
    return;
  }

  const ratio = scroller.clientWidth / scroller.scrollWidth;
  const progress = scroller.scrollLeft / maxScroll;
  const track = getPebbleProgressTrack(scroller);
  const trackWidth = track?.clientWidth || 200;
  const thumbWidth = Math.max(trackWidth * ratio, 24);

  thumb.style.width = `${thumbWidth}px`;
  thumb.style.transform = `translateX(${progress * (trackWidth - thumbWidth)}px)`;
}

function updatePebbleArrows(scroller, prev, next) {
  if (!scroller) return;
  const maxScroll = scroller.scrollWidth - scroller.clientWidth;
  const atStart = scroller.scrollLeft <= 2;
  const atEnd = scroller.scrollLeft >= maxScroll - 2;

  if (prev) prev.disabled = atStart;
  if (next) next.disabled = atEnd;
}

function scrollPebbleBy(scroller, direction) {
  const track = getPebbleTrack(scroller);
  const card = getPebbleSlide(scroller);
  if (!card) return;

  const gap = parseFloat(getComputedStyle(track || scroller).gap) || 12;
  const step = card.offsetWidth + gap;

  scroller.scrollBy({
    left: direction * step,
    behavior: 'smooth',
  });
}

function stripInlineNavControls(panel) {
  const inlineRoot = panel.closest('.pebble-collection-nav--inline');
  if (!inlineRoot) return;

  inlineRoot.querySelectorAll('.pebble-collection-nav__controls').forEach((controls) => {
    controls.remove();
  });
}

function enablePebbleDragScroll(scroller) {
  if (!scroller || scroller.pebbleDragBound) return;
  scroller.pebbleDragBound = true;

  let active = false;
  let axis = null;
  let moved = false;
  let startX = 0;
  let startY = 0;
  let startScroll = 0;
  let pointerId = null;
  const threshold = 12;

  scroller.addEventListener('dragstart', (event) => {
    event.preventDefault();
  });

  scroller.querySelectorAll('a, img').forEach((el) => {
    el.setAttribute('draggable', 'false');
  });

  scroller.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'touch') return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target.closest('button, input, select, textarea, label')) return;

    active = true;
    axis = null;
    moved = false;
    startX = event.clientX;
    startY = event.clientY;
    startScroll = scroller.scrollLeft;
    pointerId = event.pointerId;
  });

  scroller.addEventListener(
    'pointermove',
    (event) => {
      if (!active || event.pointerId !== pointerId) return;

      const dx = event.clientX - startX;
      const dy = event.clientY - startY;

      if (!axis) {
        if (Math.abs(dx) < threshold && Math.abs(dy) < threshold) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (axis === 'y') {
          active = false;
          pointerId = null;
          return;
        }

        moved = true;
        scroller.classList.add('is-dragging');
        try {
          scroller.setPointerCapture(event.pointerId);
        } catch (_) {
          /* ignore */
        }
      }

      if (axis !== 'x') return;

      scroller.scrollLeft = startScroll - dx;
      event.preventDefault();
    },
    { passive: false }
  );

  const endDrag = (event) => {
    if (!active || (event && event.pointerId !== pointerId)) return;

    active = false;
    axis = null;
    scroller.classList.remove('is-dragging');

    if (moved) {
      const blockClick = (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        scroller.removeEventListener('click', blockClick, true);
      };
      scroller.addEventListener('click', blockClick, true);
    }

    moved = false;
    pointerId = null;
  };

  scroller.addEventListener('pointerup', endDrag);
  scroller.addEventListener('pointercancel', endDrag);
  scroller.addEventListener('lostpointercapture', endDrag);
}

function lockHorizontalScrollOnFocus(scroller) {
  if (!scroller || scroller.pebbleFocusScrollLocked) return;
  scroller.pebbleFocusScrollLocked = true;

  let saved = scroller.scrollLeft || 0;

  scroller.addEventListener('pointerdown', () => {
    saved = scroller.scrollLeft || 0;
  }, true);

  scroller.addEventListener('focusin', () => {
    scroller.scrollLeft = saved;
    window.requestAnimationFrame(() => {
      scroller.scrollLeft = saved;
    });
  });
}

function bindPebblePanel(panel) {
  const scroller = panel.querySelector('[data-pebble-scroller]');
  if (!scroller || scroller.pebbleBound) return;
  scroller.pebbleBound = true;

  stripInlineNavControls(panel);

  const isInlineNav = Boolean(panel.closest('.pebble-collection-nav--inline'));
  lockHorizontalScrollOnFocus(scroller);
  // Nav collections : pas de drag souris — setPointerCapture cassait les <a href> SEO.
  // Home featured collections : drag souris + tactile (axis lock, comme les carrousels produits).
  if (!isInlineNav) {
    enablePebbleDragScroll(scroller);
  }

  const thumb = isInlineNav ? null : getPebbleProgressThumb(panel);
  const prev = panel.querySelector('[data-pebble-prev]');
  const next = panel.querySelector('[data-pebble-next]');

  const refresh = () => {
    updatePebbleProgress(scroller, thumb);
    updatePebbleArrows(scroller, prev, next);
  };

  scroller.addEventListener('scroll', refresh, { passive: true });
  window.addEventListener('resize', refresh);

  if (prev) {
    prev.addEventListener('click', () => scrollPebbleBy(scroller, -1));
  }

  if (next) {
    next.addEventListener('click', () => scrollPebbleBy(scroller, 1));
  }

  const progressTrack = getPebbleProgressTrack(scroller);
  if (progressTrack && !progressTrack.pebbleSeekBound) {
    progressTrack.pebbleSeekBound = true;
    progressTrack.style.cursor = 'pointer';
    progressTrack.addEventListener('click', (event) => {
      const rect = progressTrack.getBoundingClientRect();
      if (!rect.width) return;
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      if (maxScroll <= 0) return;
      const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
      scroller.scrollTo({
        left: ratio * maxScroll,
        behavior: 'smooth',
      });
    });
  }

  refresh();

  const activeSlide = panel.querySelector('.pebble-collection-nav__slide.is-active');
  if (activeSlide) {
    const slideLeft = activeSlide.offsetLeft;
    const slideRight = slideLeft + activeSlide.offsetWidth;
    const viewLeft = scroller.scrollLeft;
    const viewRight = viewLeft + scroller.clientWidth;
    if (slideLeft < viewLeft || slideRight > viewRight) {
      scroller.scrollTo({
        left: slideLeft - (scroller.clientWidth - activeSlide.offsetWidth) / 2,
        behavior: 'auto',
      });
      refresh();
    }
  }
}

function bindPebbleTabs(section) {
  const tabs = section.querySelectorAll('[data-pebble-tab]');
  const panels = section.querySelectorAll('[data-pebble-panel]');
  if (!tabs.length) return;

  tabs.forEach((tab) => {
    if (tab.pebbleTabBound) return;
    tab.pebbleTabBound = true;

    tab.addEventListener('click', () => {
      const target = tab.dataset.pebbleTab;
      if (!target || tab.classList.contains('is-active')) return;

      tabs.forEach((item) => {
        const active = item === tab;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      panels.forEach((panel) => {
        const active = panel.dataset.pebblePanel === target;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });

      const activePanel = section.querySelector(`[data-pebble-panel="${target}"]`);
      if (activePanel) bindPebblePanel(activePanel);
    });
  });
}

function initPebbleCollections() {
  document.querySelectorAll('.pebble-collection-nav--inline .pebble-collection-nav__controls').forEach((controls) => {
    controls.remove();
  });

  document.querySelectorAll('.featured-collections-1--pebble').forEach((section) => {
    bindPebbleTabs(section);
    section.querySelectorAll('.pebble-collections__panel.is-active').forEach(bindPebblePanel);
  });

  document.querySelectorAll('.pebble-collection-nav').forEach((section) => {
    section.querySelectorAll('[data-pebble-panel]').forEach(bindPebblePanel);
  });

  document.querySelectorAll('.main-collection--pebble [data-pebble-panel]').forEach(bindPebblePanel);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPebbleCollections);
} else {
  initPebbleCollections();
}

document.addEventListener('shopify:section:load', (event) => {
  const featured = event.target.querySelector('.featured-collections-1--pebble');
  if (featured) {
    bindPebbleTabs(featured);
    featured.querySelectorAll('[data-pebble-panel]').forEach(bindPebblePanel);
    return;
  }

  const navRoot = event.target.querySelector('.pebble-collection-nav') || event.target.querySelector('.main-collection--pebble');
  if (navRoot) {
    navRoot.querySelectorAll('[data-pebble-panel]').forEach(bindPebblePanel);
  }
});
